// frontend\src\pages\DevTimerSetup\DevTimerSetup.js
import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AGITATION_TYPES,
  processPresets,
  allStages,
  defaultAgitation,
} from "./constants";
import StageCard from "./StageCard";
import ScrollToTopButton from "../../components/ScrollToTopButton";

const DevTimerSetup = () => {
  const [processType, setProcessType] = useState("bw");
  const [stages, setStages] = useState(() => {
    const initialStages = {};
    Object.keys(allStages).forEach((key) => {
      initialStages[key] = {
        ...allStages[key],
        enabled: false,
        minutes: 0,
        seconds: 0,
        agitationType: AGITATION_TYPES.STANDARD,
        agitation: null,
      };
    });
    return initialStages;
  });
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [advanceDelay, setAdvanceDelay] = useState(5);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // === Валидация ===
  const validateStageParams = useCallback((stagesToValidate) => {
    const newErrors = {};

    Object.entries(stagesToValidate).forEach(([stageKey, stage]) => {
      if (!stage.enabled) return;

      const stageErrors = [];
      const totalSeconds = stage.minutes * 60 + stage.seconds;

      if (stage.minutes < 0 || stage.minutes > 120) {
        stageErrors.push("Минуты должны быть от 0 до 120");
      }

      if (stage.seconds < 0 || stage.seconds > 59) {
        stageErrors.push("Секунды должны быть от 0 до 59");
      }

      if (stage.agitationType === AGITATION_TYPES.STANDARD && stage.agitation) {
        const { agitation } = stage;

        if (totalSeconds < 180) {
          if (agitation.firstMinute || agitation.lastMinute) {
            stageErrors.push(
              "Для этапов <3 минут не используйте агитацию для первой/последней минуты"
            );
          }
        }

        if (agitation.interval < 5 || agitation.interval > 300) {
          stageErrors.push("Интервал агитации: от 5 до 300 секунд");
        }

        if (agitation.duration < 3 || agitation.duration > 60) {
          stageErrors.push("Длительность агитации: от 3 до 60 секунд");
        }

        if (
          agitation.interval &&
          agitation.duration &&
          agitation.duration > agitation.interval
        ) {
          stageErrors.push(
            "Длительность агитации не может быть больше интервала"
          );
        }
      }

      if (
        stage.agitationType === AGITATION_TYPES.ROTARY &&
        stage.agitation &&
        stage.agitation.duration < 3
      ) {
        stageErrors.push(
          "Длительность ротационной агитации должна быть от 3 до 60 секунд"
        );
      }

      if (stageErrors.length > 0) {
        newErrors[stageKey] = stageErrors;
      }
    });

    return newErrors;
  }, []);

  // === Инициализация пресетов ===
  const initializeStages = useCallback((type) => {
    const preset = processPresets[type];
    const newStages = {};

    Object.keys(allStages).forEach((key) => {
      const presetStage = preset?.stages?.[key] || {};

      newStages[key] = {
        ...allStages[key],
        enabled: presetStage.enabled ?? false,
        minutes: presetStage.minutes ?? 0,
        seconds: presetStage.seconds ?? 0,
        agitationType: presetStage.agitationType ?? AGITATION_TYPES.STANDARD,
        agitation:
          presetStage.agitationType !== AGITATION_TYPES.STAND
            ? presetStage.agitation
              ? { ...presetStage.agitation }
              : { ...defaultAgitation }
            : null,
      };
    });

    return newStages;
  }, []);

  // === Обработчики ===
  const handleProcessTypeChange = useCallback(
    (type) => {
      const newStages = initializeStages(type);
      setProcessType(type);
      setStages(newStages);
      setErrors({});
    },
    [initializeStages]
  );

  // Прямой вызов — без debounce
  const handleChange = useCallback((stageKey, field, value) => {
    setStages((prev) => ({
      ...prev,
      [stageKey]: {
        ...prev[stageKey],
        [field]: value,
      },
    }));
  }, []);

  const handleAgitationChange = useCallback((stageKey, field, value) => {
    setStages((prev) => {
      const stage = prev[stageKey];
      const newAgitation = { ...(stage.agitation || {}) };

      if (value === null || value === "") {
        delete newAgitation[field];
        if (Object.keys(newAgitation).length === 0) {
          return {
            ...prev,
            [stageKey]: { ...stage, agitation: null },
          };
        }
      } else {
        newAgitation[field] = value;
      }

      return {
        ...prev,
        [stageKey]: { ...stage, agitation: newAgitation },
      };
    });
  }, []);

  const handleAgitationTypeChange = useCallback((stageKey, value) => {
    setStages((prev) => {
      const currentStage = prev[stageKey];
      let newAgitation = null;

      if (value === AGITATION_TYPES.ROTARY) {
        newAgitation = { duration: 10 };
      } else if (value === AGITATION_TYPES.STANDARD) {
        newAgitation = { ...defaultAgitation };
      }

      return {
        ...prev,
        [stageKey]: {
          ...currentStage,
          agitationType: value,
          agitation: newAgitation,
        },
      };
    });
  }, []);

  const toggleStage = useCallback((stageKey) => {
    setStages((prev) => ({
      ...prev,
      [stageKey]: { ...prev[stageKey], enabled: !prev[stageKey].enabled },
    }));
  }, []);

  const startProcess = useCallback(() => {
    const enabledStages = Object.entries(stages)
      .filter(([_, stage]) => stage.enabled)
      .map(([key, stage]) => ({ key, ...stage }));

    if (enabledStages.length === 0) {
      toast.error("Выберите хотя бы один этап!");
      return;
    }

    const validationErrors = validateStageParams(stages);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([stageKey, errors]) => {
        const stageName = allStages[stageKey]?.name || `Этап ${stageKey}`;
        toast.error(`${stageName}: ${errors.join("; ")}`, {
          autoClose: 5000,
        });
      });
      return;
    }

    navigate("/timer", {
      state: {
        stages: enabledStages,
        autoAdvance,
        advanceDelay,
        processType,
      },
    });
  }, [
    stages,
    autoAdvance,
    advanceDelay,
    processType,
    navigate,
    validateStageParams,
  ]);

  // === Группировка этапов ===
  const getStageGroups = useCallback(() => {
    const groups = {
      main: processType.includes("bw")
        ? ["preSoak", "developer", "stopBath", "fixer"]
        : ["preSoak"],
      bwReversal: [
        "washAfterFix",
        "bleach",
        "reexposure",
        "redevelopment",
        "secondFixer",
      ],
      wash: ["wash"],
      e6: [
        "firstDeveloper",
        "wash",
        "reversalBath",
        "colorDeveloper",
        "preBleach",
      ],
      c41: ["developer", "bleach", "wash", "fixer"],
      final: ["hypoClear", "finalWash", "stabilizer", "wettingAgent"],
    };

    const visibleGroups = [];

    visibleGroups.push({
      title: "Основные этапы",
      stages: groups.main.filter((key) => stages[key]),
    });

    if (processType === "bw") {
      visibleGroups.push({
        title: null,
        stages: groups.wash.filter((key) => stages[key]),
      });
    }

    if (processType === "bw_reversal") {
      visibleGroups.push({
        title: "Этапы обращения",
        stages: groups.bwReversal.filter((key) => stages[key]),
      });
    }

    if (processType === "e6") {
      visibleGroups.push({
        title: "Этапы E-6",
        stages: groups.e6.filter((key) => stages[key]),
      });
    }

    if (processType === "c41") {
      visibleGroups.push({
        title: "Этапы C-41",
        stages: groups.c41.filter((key) => stages[key]),
      });
    }

    visibleGroups.push({
      title: "Финальные этапы",
      stages: groups.final.filter((key) => stages[key]),
    });

    return visibleGroups;
  }, [stages, processType]);

  const stageGroups = useMemo(() => getStageGroups(), [getStageGroups]);

  useEffect(() => {
    handleProcessTypeChange("bw");
  }, [handleProcessTypeChange]);

  return (
    <div className="container py-4">
      <h1 className="mb-4">Настройка процесса проявки</h1>

      {/* Тип процесса */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>Тип процесса</h5>
        </div>
        <div className="card-body">
          <div className="btn-group flex-wrap" role="group">
            {Object.entries(processPresets).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                className={`btn ${
                  processType === key ? "btn-primary" : "btn-outline-primary"
                } m-1`}
                onClick={() => handleProcessTypeChange(key)}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Автопереход */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>Дополнительные настройки</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={autoAdvance}
                  onChange={() => setAutoAdvance(!autoAdvance)}
                  style={{ transform: "scale(1.5)" }}
                />
                <label className="form-check-label h5 mb-0 ms-2">
                  Переключать автоматически
                </label>
              </div>
            </div>
            {autoAdvance && (
              <div className="col-md-6">
                <label className="form-label">Задержка (секунды)</label>
                <input
                  type="number"
                  className="form-control"
                  value={advanceDelay}
                  onChange={(e) =>
                    setAdvanceDelay(Math.max(1, parseInt(e.target.value) || 5))
                  }
                  min="1"
                  inputMode="numeric"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Этапы */}
      {stageGroups.map((group, index) => (
        <div key={index}>
          {group.title && <h2 className="mb-4">{group.title}</h2>}
          {group.stages.map((stageKey) => (
            <StageCard
              key={stageKey}
              stageKey={stageKey}
              params={stages[stageKey]}
              toggleStage={toggleStage}
              handleChange={handleChange}
              handleAgitationChange={handleAgitationChange}
              handleAgitationTypeChange={handleAgitationTypeChange}
              errors={errors}
            />
          ))}
        </div>
      ))}

      <div className="text-center mt-4">
        <button className="btn btn-primary btn-lg px-5" onClick={startProcess}>
          Запуск процесса
        </button>
      </div>
      <ScrollToTopButton />
    </div>
  );
};

export default DevTimerSetup;